import type {
  AutomationDefinition,
  AutomationEdge,
  AutomationNode,
} from '../../../types/automation';
import type { ProgramPhaseActionDef } from '../../../types/programPhases';
import { computeNodeSequenceMap } from '../automation/automationChain';

/**
 * Plain-language description of what completing a send-type phase
 * action will DO, for the drawer's confirmation modal (Part 3.1 of
 * ADMIN_TASK_FLOW_AND_DRAWER_SPEC). The modal must show the effects
 * of the automation's CURRENT published version — this module mirrors
 * the engine's own resolution so what the admin confirms is what the
 * run will execute:
 *
 *   - published graph: `versions[]` entry whose version equals
 *     `currentVersion`, falling back to the top-level nodes/edges
 *     (server/automation/engine.ts createAutomationRun);
 *   - step order: computeNodeSequenceMap (automationChain), the
 *     Start-first BFS the canvas and the engine's
 *     buildExecutionSequence both use, array order for anything
 *     unreached;
 *   - email attachments: the bound email template's carried document
 *     templates ∪ the step's own documentTemplateIds, template order
 *     first (serverEmailDocuments resolveEffectiveDocumentTemplateIds,
 *     re-implemented here as a plain union — the server module is not
 *     importable from client code).
 *
 * Pure and framework-free so it can be unit-tested without rendering.
 */

export interface EffectLine {
  text: string;
  /** Rendered bold in the modal (status / stage changes, per spec). */
  bold?: boolean;
}

export interface PhaseActionNameLookups {
  emailTemplate: (id: string) => { name?: string; subject?: string; documentTemplateIds?: string[] } | undefined;
  documentTemplateName: (id: string) => string | undefined;
}

/** The graph the engine will run for a published automation. */
export function publishedGraph(automation: AutomationDefinition): {
  nodes: AutomationNode[];
  edges: AutomationEdge[];
} {
  const targetVersion = automation.currentVersion || 1;
  const versionObj = automation.versions?.find((v) => v.version === targetVersion);
  return {
    nodes: versionObj?.nodes || automation.nodes || [],
    edges: versionObj?.edges || automation.edges || [],
  };
}

/**
 * Order action nodes in execution order. Non-action nodes (start,
 * trigger) are excluded; nodes the chain never reaches keep their
 * array order at the end.
 *
 * The ORDER itself comes from computeNodeSequenceMap — the single
 * sequencing source shared with the canvas badges and Assign
 * Automation modal, whose Start-first BFS is the same walk the
 * engine's buildExecutionSequence runs. This module previously walked
 * the chain with its own depth-first traversal, which orders a
 * branching graph differently from the engine (a branch's tail came
 * before its sibling), so the confirmation modal could describe the
 * effects in an order the run would not follow.
 */
export function sequenceNodes(nodes: AutomationNode[], edges: AutomationEdge[]): AutomationNode[] {
  const sequenceMap = computeNodeSequenceMap(nodes, edges || []);
  return nodes
    .map((node, arrayIndex) => ({ node, arrayIndex }))
    .filter(({ node }) => node.data?.actionType)
    .sort(
      (a, b) =>
        (sequenceMap.get(a.node.id) ?? Number.MAX_SAFE_INTEGER) -
          (sequenceMap.get(b.node.id) ?? Number.MAX_SAFE_INTEGER) ||
        a.arrayIndex - b.arrayIndex
    )
    .map(({ node }) => node);
}

function delayPrefix(node: AutomationNode): string {
  const v = node.data?.delayValue;
  const u = node.data?.delayUnit;
  if (typeof v === 'number' && v > 0 && u) return `After ${v} ${u}: `;
  return '';
}

function quoted(value: string): string {
  return `“${value}”`;
}

/** Effective document template ids for an email send: the template's
 *  carried ids first, then the step's own, de-duplicated. */
export function effectiveDocumentTemplateIds(
  templateCarried: string[] | undefined,
  stepLevel: string[] | undefined
): string[] {
  const out: string[] = [];
  for (const id of [...(templateCarried || []), ...(stepLevel || [])]) {
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

function describeNode(node: AutomationNode, lookups: PhaseActionNameLookups): EffectLine[] {
  const cfg = node.data?.config || {};
  const prefix = delayPrefix(node);
  const lines: EffectLine[] = [];
  const push = (text: string, bold?: boolean) => lines.push({ text: prefix + text, bold });

  switch (node.data.actionType) {
    case 'generate_document': {
      const name = cfg.templateName || (cfg.templateId ? lookups.documentTemplateName(cfg.templateId) : undefined) || 'a document';
      push(
        cfg.saveToApplicantDocs === false
          ? `Generate the document ${quoted(name)}`
          : `Generate the document ${quoted(name)} and save it to the applicant’s documents`
      );
      break;
    }
    case 'send_sign_request': {
      const name = cfg.signingTemplateName || cfg.signingTemplateId || 'a signing template';
      push(`Send native e-signature request using template ${quoted(name)}`);
      break;
    }
    case 'send_email': {
      const tpl = cfg.emailTemplateId ? lookups.emailTemplate(cfg.emailTemplateId) : undefined;
      const name = tpl?.name || 'an email';
      const subject = cfg.subject || tpl?.subject;
      push(subject ? `Send the email ${quoted(name)} — subject ${quoted(subject)}` : `Send the email ${quoted(name)}`);
      const attachIds = effectiveDocumentTemplateIds(tpl?.documentTemplateIds, cfg.documentTemplateIds);
      if (attachIds.length > 0) {
        const names = attachIds.map((id) => lookups.documentTemplateName(id) || id);
        push(`Attach generated document${names.length > 1 ? 's' : ''}: ${names.map(quoted).join(', ')}`);
      }
      break;
    }
    case 'create_event': {
      const title = cfg.eventTitle || node.data.label || 'an event';
      let when = '';
      if (cfg.eventStartMode === 'fixed' && cfg.eventDateTime) {
        const d = new Date(cfg.eventDateTime);
        when = Number.isNaN(d.getTime()) ? '' : ` on ${d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`;
      } else if (cfg.eventStartMode === 'relative' && cfg.eventStartInValue && cfg.eventStartInUnit) {
        when = `, ${cfg.eventStartInValue} ${cfg.eventStartInUnit} after it runs`;
      } else {
        when = ' (date to be scheduled)';
      }
      push(`Create the event ${quoted(title)}${when}`);
      break;
    }
    case 'create_invoice': {
      push('Create an invoice for the applicant from the Finance template');
      if (cfg.sendInvoiceEmail) push('Email the invoice to the applicant');
      break;
    }
    case 'create_quote': {
      push('Create a quote for the applicant from the Finance template');
      if (cfg.sendQuoteEmail) push('Email the quote to the applicant');
      break;
    }
    case 'create_support_ticket': {
      push(`Create a support ticket${cfg.ticketTitle ? `: ${quoted(cfg.ticketTitle)}` : ''}`);
      break;
    }
    case 'create_internal_task': {
      push(`Create an internal task${cfg.taskTitle ? `: ${quoted(cfg.taskTitle)}` : ''}`);
      break;
    }
    case 'tag_application': {
      if (cfg.tagsToAdd?.length) push(`Add tag${cfg.tagsToAdd.length > 1 ? 's' : ''}: ${cfg.tagsToAdd.join(', ')}`);
      if (cfg.tagsToRemove?.length) push(`Remove tag${cfg.tagsToRemove.length > 1 ? 's' : ''}: ${cfg.tagsToRemove.join(', ')}`);
      if (!cfg.tagsToAdd?.length && !cfg.tagsToRemove?.length) push('Update the application’s tags');
      break;
    }
    case 'change_workflow_stage': {
      push(`Move the application to the stage ${quoted(cfg.targetStageLabel || cfg.targetStageKey || 'another stage')}`, true);
      break;
    }
    case 'update_application_status': {
      push(`Change the application status to ${quoted(cfg.targetApplicationStatus || 'a new status')}`, true);
      if (cfg.sendNotificationEmail !== false) push('The portal’s usual status notification email is also sent');
      break;
    }
    case 'add_to_batch':
    case 'update_batch': {
      const batch = cfg.batchName || 'a batch';
      if (cfg.batchOperation === 'remove_member') push(`Remove the applicant from every batch they belong to`);
      else if (cfg.batchOperation === 'replace_all') push(`Set the membership of the batch ${quoted(batch)} to the selected applicants`);
      else push(`Add the applicant to the batch ${quoted(batch)}`);
      break;
    }
    case 'update_admin': {
      const who = cfg.adminTargetName || 'an administrator';
      push(`Update the administrator ${quoted(who)}${cfg.adminField ? ` (${cfg.adminField})` : ''}`);
      break;
    }
    default:
      push(node.data.label || 'Run a step');
  }
  return lines;
}

/** Ordered plain-language effects of running an automation. */
export function describeAutomationEffects(
  automation: AutomationDefinition,
  lookups: PhaseActionNameLookups
): EffectLine[] {
  const { nodes, edges } = publishedGraph(automation);
  return sequenceNodes(nodes, edges).flatMap((n) => describeNode(n, lookups));
}

/** Effects of a direct send_email phase action (definition binding). */
export function describeEmailActionEffects(
  def: ProgramPhaseActionDef,
  lookups: PhaseActionNameLookups
): EffectLine[] {
  const cfg = def.config || {};
  const tpl = cfg.emailTemplateId ? lookups.emailTemplate(cfg.emailTemplateId) : undefined;
  const lines: EffectLine[] = [];
  if (cfg.emailTemplateId) {
    const subject = cfg.emailSubject || tpl?.subject;
    lines.push({
      text: subject
        ? `Send the email ${quoted(tpl?.name || 'the selected template')} — subject ${quoted(subject)}`
        : `Send the email ${quoted(tpl?.name || 'the selected template')}`,
    });
  } else {
    lines.push({
      text: cfg.emailSubject
        ? `Send the email written on this task — subject ${quoted(cfg.emailSubject)}`
        : 'Send the email written on this task',
    });
  }
  const attachIds = effectiveDocumentTemplateIds(tpl?.documentTemplateIds, cfg.documentTemplateIds);
  if (attachIds.length > 0) {
    const names = attachIds.map((id) => lookups.documentTemplateName(id) || id);
    lines.push({ text: `Attach generated document${names.length > 1 ? 's' : ''}: ${names.map(quoted).join(', ')}` });
  }
  return lines;
}
